import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { CompanyDto, VehicleSpecialBondRequestDto } from 'dtos'
import { VehicleSpecialBondRequestValidationSchema } from 'dtos/vehicleSpecialBondRequestDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useState } from 'react'
import { useDebounce } from 'utils'

interface BondSpecialRequestAddEditDialogProps {
  disabled?: boolean
  initialValues: VehicleSpecialBondRequestDto
  open: boolean
  onClose: () => void
  onSave: (values: any) => void
  isLoading: boolean
}

export default function BondSpecialRequestAddEditDialog({
  disabled,
  initialValues,
  isLoading,
  onClose,
  onSave,
  open
}: BondSpecialRequestAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingInsuranceCompanies: false,
    isGettingInsuranceCompanyAddresses: false
  })
  const [insuranceCompanies, setInsuranceCompanies] = useState<CompanyDto[]>([])
  const [insuranceCompanyAddressEmails, setInsuranceCompanyAddressEmails] = useState<string[]>([])

  const getInsuranceCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingInsuranceCompanies', true)
    controllers
      .getCompanies({
        junction: Junction.AND,
        order: 'asc',
        orderBy: 'name',
        page: 0,
        pageSize: AUTOCOMPLETE_PAGESIZE,
        search,
        statuses: ['isActive', 'isInsuranceCompany']
      })
      .then(response => {
        setInsuranceCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingInsuranceCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={VehicleSpecialBondRequestValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12}>
                    <DialogTitle>Special Bond Request</DialogTitle>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={insuranceCompany => insuranceCompany.name!}
                      noOptionsText={
                        loadingState.isGettingInsuranceCompanies
                          ? 'Searching Insurance Companies...'
                          : 'No Insurance Companies. Start typing to search for Insurance Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('insuranceCompany', value)
                        setFieldValue('insuranceCompanyEmail', null)

                        if (value?.companyGuid) {
                          setLoadingState('isGettingInsuranceCompanyAddresses', true)
                          controllers
                            .getCompanyAddresses(value?.companyGuid!, {
                              junction: Junction.AND,
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              statuses: ['isActive', 'IsInsuranceUnderwriterCompanyAddress']
                            })
                            .then(response => {
                              setInsuranceCompanyAddressEmails(
                                response.value
                                  ?.filter(companyAddress => companyAddress.email)
                                  ?.map(companyAddress => companyAddress.email!)
                              )
                            })
                            .finally(() => {
                              setLoadingState('isGettingInsuranceCompanyAddresses', false)
                            })
                        }
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getInsuranceCompaniesAutocomplete(value)
                      }}
                      options={insuranceCompanies?.map(insuranceCompany => ({
                        companyGuid: insuranceCompany.companyGuid,
                        name: insuranceCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.insuranceCompany && errors.insuranceCompany)}
                          helperText={touched.insuranceCompany && errors.insuranceCompany}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingInsuranceCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Insurance Company'
                        />
                      )}
                      value={
                        values.insuranceCompany
                          ? { companyGuid: values.insuranceCompany.companyGuid, name: values.insuranceCompany.name }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      noOptionsText={
                        loadingState.isGettingInsuranceCompanyAddresses
                          ? 'Searching Insurance Company Address Emails...'
                          : 'No Insurance Company Address Emails.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('insuranceCompanyEmail', value)
                      }}
                      options={insuranceCompanyAddressEmails}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.insuranceCompanyEmail && errors.insuranceCompanyEmail)}
                          helperText={touched.insuranceCompanyEmail && errors.insuranceCompanyEmail}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingInsuranceCompanyAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Insurance Company Email'
                        />
                      )}
                      value={values.insuranceCompanyEmail || undefined}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting || disabled}
                      error={Boolean(touched.specialNotes && errors.specialNotes)}
                      fullWidth
                      helperText={touched.specialNotes && errors.specialNotes}
                      label='Special Notes'
                      multiline
                      name='specialNotes'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      rows={4}
                      value={values.specialNotes}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <ExplanationAccordion variant='outlined'>
                    The Special Notes field contents will be added to the body of the Special Bond request email. This email
                    will automatically display the vehicle's VIN, Year, Make, Model, and Amount (USD) and the name and email
                    address of the user who pressed “Send”. ||Information entered in this dialog will be displayed on the
                    Special Bond Request card on the Bond tab.
                  </ExplanationAccordion>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton disabled={disabled} loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SEND
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
